import { useCallback, useState } from 'react';
import { Button, Input, Typography, Space, Tag, Popconfirm, Badge, Tooltip } from 'antd';
import {
  PlusOutlined, MessageOutlined, DeleteOutlined,
  EditOutlined, QuestionCircleOutlined, OrderedListOutlined,
  ThunderboltOutlined, RobotOutlined, CrownOutlined, GiftOutlined,
} from '@ant-design/icons';
import { useChatStore, ChatMode } from '@/stores/chat';

const { Text } = Typography;

const modeConfig: Record<ChatMode, { label: string; icon: React.ReactNode; color: string; desc: string }> = {
  qa: { label: '问答模式', icon: <QuestionCircleOutlined />, color: '#6366f1', desc: '一问一答，获取精准答案' },
  plan: { label: '计划模式', icon: <OrderedListOutlined />, color: '#f59e0b', desc: 'AI 先规划步骤，再逐步执行' },
  execute: { label: '直接执行', icon: <ThunderboltOutlined />, color: '#10b981', desc: '跳过规划，直接操作工具执行' },
};

export default function ChatSidebar({ onOpenCreateAgent }: { onOpenCreateAgent?: () => void }) {
  const {
    sessions, activeSessionId, mode, createSession,
    switchSession, deleteSession, renameSession, setMode, quota,
  } = useChatStore();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const handleNewSession = useCallback(() => {
    createSession();
  }, [createSession]);

  const handleStartRename = useCallback((id: string, title: string) => {
    setEditingId(id);
    setEditTitle(title);
  }, []);

  const handleFinishRename = useCallback(() => {
    if (editingId && editTitle.trim()) {
      renameSession(editingId, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle('');
  }, [editingId, editTitle, renameSession]);

  // 按时间分组会话
  const today = sessions.filter((s) => Date.now() - s.createdAt < 86400000);
  const yesterday = sessions.filter(
    (s) => Date.now() - s.createdAt >= 86400000 && Date.now() - s.createdAt < 172800000
  );
  const older = sessions.filter((s) => Date.now() - s.createdAt >= 172800000);

  const member = quota && quota.plan !== 'free' && !quota.expired;

  return (
    <div className="chat-sidebar">
      {/* 新建对话 / 创建智能体 */}
      <div className="sidebar-actions">
        <Button
          type="primary"
          icon={<PlusOutlined />}
          block
          onClick={handleNewSession}
          className="btn-new-session"
        >
          新对话
        </Button>
        <Button
          block
          icon={<RobotOutlined />}
          onClick={onOpenCreateAgent}
          className="btn-agent"
        >
          创建智能体
        </Button>
      </div>

      {/* 模式切换 */}
      <div className="sidebar-section">
        <Text className="sidebar-label">对话模式</Text>
        <div className="mode-list">
          {(Object.keys(modeConfig) as ChatMode[]).map((m) => (
            <Tooltip key={m} title={modeConfig[m].desc} placement="right">
              <div
                className={`mode-item ${mode === m ? 'active' : ''}`}
                onClick={() => setMode(m)}
              >
                <span className="mode-icon" style={{ color: modeConfig[m].color }}>
                  {modeConfig[m].icon}
                </span>
                <span className="mode-label">{modeConfig[m].label}</span>
                {mode === m && <Badge status="processing" className="mode-active-dot" />}
              </div>
            </Tooltip>
          ))}
        </div>
      </div>

      {/* 历史会话 */}
      <div className="session-area">
        <Text className="sidebar-label">历史会话</Text>
        <div className="session-scroll">
          {renderSessionGroup('今天', today)}
          {renderSessionGroup('昨天', yesterday)}
          {renderSessionGroup('更早', older)}
          {sessions.length === 0 && (
            <div className="session-empty">
              <MessageOutlined />
              <div>暂无会话</div>
            </div>
          )}
        </div>
      </div>

      {/* 底部：会员 / 积分 徽标 */}
      {quota && (
        <div className="sidebar-footer">
          <Tooltip title={member ? '当前为会员，享更高额度' : '当前为免费版，升级解锁更多额度'}>
            <span className={`member-chip${member ? ' is-member' : ''}`}>
              <CrownOutlined />
              {member ? '会员' : '免费版'}
            </span>
          </Tooltip>
          <Tooltip title="可用积分">
            <span className="credits-chip">
              <GiftOutlined />
              {quota.credits ?? 0} 积分
            </span>
          </Tooltip>
        </div>
      )}

      <style>{`
        .chat-sidebar {
          width: 272px; height: 100%; display: flex; flex-direction: column;
          background: var(--bg-sidebar); border-right: 1px solid var(--border);
          flex-shrink: 0;
        }
        .sidebar-actions { padding: 12px; display: flex; flex-direction: column; gap: 8px; }
        .btn-new-session {
          height: 38px; border-radius: 10px; font-weight: 600;
          background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none;
          box-shadow: 0 2px 10px rgba(99, 102, 241, 0.28);
        }
        .btn-agent { height: 36px; border-radius: 10px; color: var(--text-secondary); }
        .sidebar-section { padding: 0 12px 10px; }
        .sidebar-label {
          display: block; font-size: 11px; color: var(--text-tertiary);
          text-transform: uppercase; letter-spacing: 1px; padding: 0 4px 6px;
        }
        .mode-list { display: flex; flex-direction: column; gap: 2px; }
        .mode-item {
          display: flex; align-items: center; gap: 8px;
          padding: 7px 10px; border-radius: 8px; cursor: pointer;
          font-size: 13px; transition: all 0.15s; color: var(--text-secondary);
        }
        .mode-item:hover { background: var(--bg-sidebar-hover); }
        .mode-item.active { background: rgba(99, 102, 241, 0.12); color: var(--text-primary); font-weight: 600; }
        .mode-icon { font-size: 15px; }
        .mode-active-dot { margin-left: auto; }
        .session-area { flex: 1; overflow: hidden; display: flex; flex-direction: column; min-height: 0; }
        .session-scroll { flex: 1; overflow-y: auto; padding: 0 6px 8px; }
        .session-group-title {
          display: block; font-size: 11px; color: var(--text-tertiary);
          padding: 6px 10px 2px;
        }
        .session-item {
          padding: 7px 10px; border-radius: 8px; cursor: pointer;
          font-size: 13px; display: flex; align-items: center; justify-content: space-between;
          transition: all 0.15s; margin: 1px 0; color: var(--text-primary);
          border: 1px solid transparent;
        }
        .session-item:hover { background: var(--bg-sidebar-hover); }
        .session-item.active {
          background: rgba(99, 102, 241, 0.10);
          border-color: rgba(99, 102, 241, 0.25);
        }
        .session-actions { opacity: 0; transition: opacity 0.15s; }
        .session-item:hover .session-actions { opacity: 1; }
        .session-empty { text-align: center; padding: 28px 0; color: var(--text-tertiary); font-size: 13px; }
        .session-empty svg { font-size: 22px; margin-bottom: 8px; }
        .sidebar-footer {
          display: flex; gap: 6px; align-items: center; padding: 10px 12px;
          border-top: 1px solid var(--border);
        }
        .member-chip, .credits-chip {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 12px; padding: 3px 10px; border-radius: 20px;
          border: 1px solid var(--border); color: var(--text-secondary);
        }
        .member-chip.is-member {
          background: linear-gradient(135deg, rgba(99,102,241,0.16), rgba(139,92,246,0.16));
          border-color: rgba(99,102,241,0.35); color: var(--text-primary);
        }
      `}</style>
    </div>
  );

  function renderSessionGroup(title: string, list: typeof sessions) {
    if (list.length === 0) return null;
    return (
      <div style={{ marginBottom: 4 }}>
        <Text className="session-group-title">{title}</Text>
        {list.map((s) => (
          <div
            key={s.id}
            className={`session-item ${s.id === activeSessionId ? 'active' : ''}`}
            onClick={() => switchSession(s.id)}
          >
            {editingId === s.id ? (
              <Input
                size="small"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onPressEnter={handleFinishRename}
                onBlur={handleFinishRename}
                autoFocus
                style={{ flex: 1, fontSize: 13 }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.title}
              </span>
            )}
            {editingId !== s.id && (
              <Space size={2} className="session-actions">
                <Button
                  type="text" size="small"
                  icon={<EditOutlined style={{ fontSize: 11 }} />}
                  onClick={(e) => { e.stopPropagation(); handleStartRename(s.id, s.title); }}
                />
                <Popconfirm
                  title="删除此会话？"
                  onConfirm={(e) => { e?.stopPropagation(); deleteSession(s.id); }}
                  onCancel={(e) => e?.stopPropagation()}
                >
                  <Button
                    type="text" size="small" danger
                    icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </Space>
            )}
          </div>
        ))}
      </div>
    );
  }
}
